import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

export type NonBaseMood = 'calm' | 'energetic' | 'hyper' | 'serious'
export type Mood = 'base' | NonBaseMood | 'random'

export interface MoodMixing {
  primaryMood: NonBaseMood
  secondaryMood: NonBaseMood
  mixCoefficient: number // 0 to 1, where 0 is pure primary and 1 is pure secondary
}

export interface PowerMixing {
  primaryPower: 1 | 2 | 3
  secondaryPower: 1 | 2 | 3
  mixCoefficient: number // 0 to 1, where 0 is pure primary and 1 is pure secondary
}

export interface MoodSelection {
  mood: Mood
  power: 1 | 2 | 3
  moodMixing?: MoodMixing
  powerMixing?: PowerMixing
}

export interface MoodCircleProps {
  onMoodSelect: (selection: MoodSelection | null) => void
  currentSelection?: MoodSelection
  availableMoods: string[]
}

const MOOD_COLORS = {
  base: 'bg-green-500',
  calm: 'bg-calm',
  energetic: 'bg-energetic',
  hyper: 'bg-hyper',
  serious: 'bg-serious'
} as const

// Helper functions to position the dot
const getMoodAngle = (mood: MoodSelection['mood']): number => {
  const angles = {
    base: 0,
    energetic: 0,
    hyper: Math.PI / 2,
    calm: Math.PI,
    serious: -Math.PI / 2,
    random: 0
  }
  return angles[mood]
}

const getDistanceFromPower = (power: MoodSelection['power']): number => {
  return power === 1 ? 20 : power === 2 ? 40 : 60
}

const getSelectionPosition = (selection: MoodSelection): { x: number; y: number } => {
  const angle = getMoodAngle(selection.mood)
  const distance = getDistanceFromPower(selection.power)
  
  // Convert polar coordinates to Cartesian
  const x = 50 + Math.cos(angle) * distance
  const y = 50 + Math.sin(angle) * distance
  
  return { x, y }
}

export function MoodCircle({ onMoodSelect, currentSelection, availableMoods }: MoodCircleProps) {
  const [hoveredSection, setHoveredSection] = useState<MoodSelection | null>(null)
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null)

  // Filter MOOD_COLORS to only show available moods
  const availableMoodColors = Object.entries(MOOD_COLORS)
    .filter(([mood]) => availableMoods.includes(mood))
    .reduce((acc, [mood, color]) => ({ ...acc, [mood]: color }), {} as typeof MOOD_COLORS)

  // Calculate which section would be active based on mouse position
  const calculateSection = (x: number, y: number, rect: DOMRect): MoodSelection => {
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const relX = x - centerX
    const relY = y - centerY
    
    // Calculate angle in degrees (0 is right, going counterclockwise)
    let angle = Math.atan2(relY, relX) * (180 / Math.PI)
    if (angle < 0) angle += 360
    
    // Calculate distance from center (0 to 1)
    const distance = Math.sqrt(relX * relX + relY * relY)
    const maxDistance = Math.min(rect.width, rect.height) / 2
    const relativeDistance = distance / maxDistance

    // Determine mood based on angle
    let mood: Mood = 'base'
    let moodMixing: MoodMixing | undefined
    
    if (relativeDistance <= 0.1) {
      // If in the center circle (10%), it's base mood with no mixing
      mood = 'base'
    } else {
      // Determine primary mood
      let primaryMood: NonBaseMood
      if (angle <= 45 || angle > 315) primaryMood = 'energetic'
      else if (angle <= 135) primaryMood = 'hyper'
      else if (angle <= 225) primaryMood = 'calm'
      else primaryMood = 'serious'

      // Define mood boundaries and mixing zones
      const moodBoundaries = [45, 135, 225, 315] // in degrees
      const mixingZoneSize = 35 // degrees on each side of boundary (17.5 degrees each side)

      // Find distance to nearest mood boundary
      const distanceFromMoodBoundary = moodBoundaries.map(boundary => 
        Math.min(
          Math.abs(angle - boundary),
          Math.abs((angle - 360) - boundary),
          Math.abs(angle - (boundary + 360))
        )
      ).reduce((min, dist) => Math.min(min, dist), Infinity)

      // If we're in a mixing zone
      if (distanceFromMoodBoundary <= mixingZoneSize) {
        // Find which boundary we're near
        const nearestBoundary = moodBoundaries.reduce((nearest, boundary) => {
          const dist = Math.min(
            Math.abs(angle - boundary),
            Math.abs((angle - 360) - boundary),
            Math.abs(angle - (boundary + 360))
          )
          return dist < Math.abs(angle - nearest) ? boundary : nearest
        })

        // Determine secondary mood based on which direction we're moving
        let secondaryMood: NonBaseMood
        if (nearestBoundary === 45) {
          secondaryMood = angle <= 45 ? 'hyper' : 'energetic'
        } else if (nearestBoundary === 135) {
          secondaryMood = angle <= 135 ? 'calm' : 'hyper'
        } else if (nearestBoundary === 225) {
          secondaryMood = angle <= 225 ? 'serious' : 'calm'
        } else { // 315
          secondaryMood = angle <= 315 ? 'energetic' : 'serious'
        }

        // Only set up mood mixing if both moods are available
        if (availableMoods.includes(primaryMood) && availableMoods.includes(secondaryMood)) {
          // Calculate mix coefficient (0 at edge of mixing zone, 1 at boundary)
          const mixCoefficient = 1 - (distanceFromMoodBoundary / mixingZoneSize)

          moodMixing = {
            primaryMood,
            secondaryMood,
            mixCoefficient
          }
        }
      }

      mood = primaryMood
    }

    // Determine power and power mixing based on distance from center
    let power: 1 | 2 | 3 = 1
    let powerMixing: PowerMixing | undefined

    if (mood === 'base') {
      power = 1
    } else {
      // Map distance to power levels (after the 10% base circle)
      // Normalize distance to 0-1 after base circle
      const powerDistance = (relativeDistance - 0.1) / 0.9

      // Define circle midpoints and boundaries
      const circle1Mid = 0.165 // middle of Power 1 circle (halfway between 0 and 0.33)
      const circle2Mid = 0.5   // middle of Power 2 circle (halfway between 0.33 and 0.67)
      const circle3Mid = 0.835 // middle of Power 3 circle (halfway between 0.67 and 1)

      // Determine base power level based on position
      if (powerDistance <= 0.33) {
        power = 1
      } else if (powerDistance <= 0.67) {
        power = 2
      } else {
        power = 3
      }

      // Calculate power mixing
      if (powerDistance >= circle1Mid && powerDistance <= circle2Mid) {
        // Mixing between Power 1 and 2
        const mixProgress = (powerDistance - circle1Mid) / (circle2Mid - circle1Mid)
        powerMixing = {
          primaryPower: 1,
          secondaryPower: 2,
          mixCoefficient: mixProgress
        }
        // If we're past halfway, make Power 2 primary
        if (mixProgress > 0.5) {
          power = 2
          powerMixing.primaryPower = 2
          powerMixing.secondaryPower = 1
          powerMixing.mixCoefficient = 1 - mixProgress
        }
      } else if (powerDistance >= circle2Mid && powerDistance <= circle3Mid) {
        // Mixing between Power 2 and 3
        const mixProgress = (powerDistance - circle2Mid) / (circle3Mid - circle2Mid)
        powerMixing = {
          primaryPower: 2,
          secondaryPower: 3,
          mixCoefficient: mixProgress
        }
        // If we're past halfway, make Power 3 primary
        if (mixProgress > 0.5) {
          power = 3
          powerMixing.primaryPower = 3
          powerMixing.secondaryPower = 2
          powerMixing.mixCoefficient = 1 - mixProgress
        }
      }
    }

    return { mood, power, moodMixing, powerMixing }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setMousePosition({ x: e.clientX, y: e.clientY })
    setHoveredSection(calculateSection(e.clientX, e.clientY, rect))
  }

  const handleMouseLeave = () => {
    setHoveredSection(null)
    setMousePosition(null)
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const selection = calculateSection(e.clientX, e.clientY, rect)
    onMoodSelect(selection)
  }

  // Helper function to determine if a section is active
  const isSectionActive = (mood: MoodSelection['mood'], power: MoodSelection['power']) => {
    return currentSelection?.mood === mood && currentSelection?.power === power
  }

  // Helper function to determine if a section is hovered
  const isSectionHovered = (mood: MoodSelection['mood'], power: MoodSelection['power']) => {
    return hoveredSection?.mood === mood && hoveredSection?.power === power
  }

  return (
    <div className="relative w-96 h-96">
      {/* Selection info */}
      {(currentSelection || hoveredSection) && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-800 whitespace-nowrap">
          {hoveredSection ? (
            <div className="flex flex-col items-center">
              <div>
                {hoveredSection.mood === 'base' ? (
                  `Base (Power 1)`
                ) : (
                  `${hoveredSection.mood} (Power ${hoveredSection.power})`
                )}
              </div>
            </div>
          ) : currentSelection && (
            <div className="flex flex-col items-center">
              <div>
                {currentSelection.mood === 'base' ? (
                  `Base (Power 1)`
                ) : (
                  `${currentSelection.mood} (Power ${currentSelection.power})`
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Base circle with sections */}
      <div 
        className="absolute inset-0 rounded-full cursor-pointer overflow-hidden"
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Power level rings */}
        <div className="absolute inset-0">
          {/* Center (base) circle */}
          <div className={`absolute left-1/2 top-1/2 w-[10%] h-[10%] -translate-x-1/2 -translate-y-1/2 rounded-full 
            transition-colors duration-200
            ${hoveredSection?.mood === 'base' ? 'bg-green-500/60' : 
              currentSelection?.mood === 'base' ? 'bg-green-500/80' : 'bg-green-500/20'}`}
          >
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm font-semibold text-green-800">
              Base
            </span>
          </div>
          
          {/* Power level rings - main boundaries */}
          <div className={`absolute left-1/2 top-1/2 w-[100%] h-[100%] -translate-x-1/2 -translate-y-1/2 rounded-full 
            border-2 ${hoveredSection?.power === 3 ? 'border-purple-400/80' : 'border-purple-400/30'}`}
          />
          <div className={`absolute left-1/2 top-1/2 w-[67%] h-[67%] -translate-x-1/2 -translate-y-1/2 rounded-full 
            border-2 ${hoveredSection?.power === 2 ? 'border-purple-400/80' : 'border-purple-400/30'}`}
          />
          <div className={`absolute left-1/2 top-1/2 w-[33%] h-[33%] -translate-x-1/2 -translate-y-1/2 rounded-full 
            border-2 ${hoveredSection?.power === 1 ? 'border-purple-400/80' : 'border-purple-400/30'}`}
          />

          {/* Power level labels */}
          <span className="absolute left-1/2 top-[25%] -translate-x-1/2 -translate-y-1/2 text-xs font-medium text-purple-400">
            P1
          </span>
          <span className="absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 text-xs font-medium text-purple-400">
            P2
          </span>
          <span className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 text-xs font-medium text-purple-400">
            P3
          </span>
        </div>

        {/* Mood sections */}
        <div className="absolute inset-0">
          {/* Energetic section (right) */}
          <div className="absolute top-0 right-0 w-1/2 h-full">
            <div className={`w-full h-full bg-gradient-to-l 
              ${hoveredSection?.mood === 'energetic' ? 'from-energetic/80' : 'from-energetic/20'}`}
            />
            <span className="absolute right-[30%] top-1/2 -translate-y-1/2 text-lg font-semibold text-energetic rotate-90">
              Energetic
            </span>
          </div>
          
          {/* Angle visualization lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            {/* Energetic-Hyper mixing zone */}
            <line x1="50%" y1="50%" x2="85%" y2="85%" 
                  className={`stroke-purple-400/30 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`} 
                  strokeWidth="1" strokeDasharray="4 4" />
            <text x="78%" y="78%" className={`text-xs fill-purple-400/60 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`}>45°</text>
            {/* Mixing boundaries */}
            <line x1="50%" y1="50%" x2="92.5%" y2="67.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="85%" y="62%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`}>27.5°</text>
            <line x1="50%" y1="50%" x2="67.5%" y2="92.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="60%" y="88%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('hyper') ? 'opacity-10' : ''}`}>62.5°</text>

            {/* Hyper-Calm mixing zone */}
            <line x1="50%" y1="50%" x2="15%" y2="85%" 
                  className={`stroke-purple-400/30 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="4 4" />
            <text x="17%" y="78%" className={`text-xs fill-purple-400/60 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}>135°</text>
            {/* Mixing boundaries */}
            <line x1="50%" y1="50%" x2="32.5%" y2="92.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="35%" y="88%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}>117.5°</text>
            <line x1="50%" y1="50%" x2="7.5%" y2="67.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="10%" y="62%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('hyper') || !availableMoods.includes('calm') ? 'opacity-10' : ''}`}>152.5°</text>

            {/* Calm-Serious mixing zone */}
            <line x1="50%" y1="50%" x2="15%" y2="15%" 
                  className={`stroke-purple-400/30 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="4 4" />
            <text x="17%" y="17%" className={`text-xs fill-purple-400/60 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}>225°</text>
            {/* Mixing boundaries */}
            <line x1="50%" y1="50%" x2="7.5%" y2="32.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="10%" y="35%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}>207.5°</text>
            <line x1="50%" y1="50%" x2="32.5%" y2="7.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="35%" y="10%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('calm') || !availableMoods.includes('serious') ? 'opacity-10' : ''}`}>242.5°</text>

            {/* Serious-Energetic mixing zone */}
            <line x1="50%" y1="50%" x2="85%" y2="15%" 
                  className={`stroke-purple-400/30 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="4 4" />
            <text x="78%" y="17%" className={`text-xs fill-purple-400/60 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}>315°</text>
            {/* Mixing boundaries */}
            <line x1="50%" y1="50%" x2="67.5%" y2="7.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="60%" y="10%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}>297.5°</text>
            <line x1="50%" y1="50%" x2="92.5%" y2="32.5%" 
                  className={`stroke-purple-400/20 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}
                  strokeWidth="1" strokeDasharray="2 2" />
            <text x="85%" y="35%" className={`text-[10px] fill-purple-400/40 ${!availableMoods.includes('serious') || !availableMoods.includes('energetic') ? 'opacity-10' : ''}`}>332.5°</text>
          </svg>
          
          {/* Serious section (top) */}
          <div className="absolute top-0 left-0 w-full h-1/2">
            <div className={`w-full h-full bg-gradient-to-b 
              ${hoveredSection?.mood === 'serious' ? 'from-serious/80' : 'from-serious/20'}`}
            />
            <span className="absolute top-[30%] left-1/2 -translate-x-1/2 text-lg font-semibold text-serious">
              Serious
            </span>
          </div>
          
          {/* Calm section (left) */}
          <div className="absolute top-0 left-0 w-1/2 h-full">
            <div className={`w-full h-full bg-gradient-to-r 
              ${hoveredSection?.mood === 'calm' ? 'from-calm/80' : 'from-calm/20'}`}
            />
            <span className="absolute left-[30%] top-1/2 -translate-y-1/2 text-lg font-semibold text-calm -rotate-90">
              Calm
            </span>
          </div>
          
          {/* Hyper section (bottom) */}
          <div className="absolute bottom-0 left-0 w-full h-1/2">
            <div className={`w-full h-full bg-gradient-to-t 
              ${hoveredSection?.mood === 'hyper' ? 'from-hyper/80' : 'from-hyper/20'}`}
            />
            <span className="absolute bottom-[30%] left-1/2 -translate-x-1/2 text-lg font-semibold text-hyper">
              Hyper
            </span>
          </div>
        </div>

        {/* Hidden mixing circles and zones - preserved for future use */}
        <div className="hidden">
          {/* Power level midpoints - where mixing starts/ends */}
          <div className="w-[83.5%] h-[83.5%] rounded-full border border-dotted border-purple-400/40" />
          <div className="w-[50%] h-[50%] rounded-full border border-dotted border-purple-400/40" />
          <div className="w-[16.5%] h-[16.5%] rounded-full border border-dotted border-purple-400/40" />

          {/* Power mixing zone indicators */}
          <div className="w-[50%] h-[50%] rounded-full border-4 border-dashed border-purple-400/20" />
          <div className="w-[83.5%] h-[83.5%] rounded-full border-4 border-dashed border-purple-400/20" />

          {/* Mood mixing zone indicators */}
          <svg className="w-full h-full">
            <path d="M 192 96 A 96 96 0 0 1 248 152" className="stroke-purple-400/40 stroke-[1px] fill-none" strokeDasharray="4 4" />
            <path d="M 96 192 A 96 96 0 0 1 152 248" className="stroke-purple-400/40 stroke-[1px] fill-none" strokeDasharray="4 4" />
            <path d="M 0 96 A 96 96 0 0 1 44 152" className="stroke-purple-400/40 stroke-[1px] fill-none" strokeDasharray="4 4" />
            <path d="M 96 0 A 96 96 0 0 1 152 44" className="stroke-purple-400/40 stroke-[1px] fill-none" strokeDasharray="4 4" />
          </svg>
        </div>
      </div>
    </div>
  )
} 